import { Icon } from '@iconify/react';
import { formatCurrency, formatDate } from '../components/helpers';

const balanceLabel = (val) => {
    const v = parseFloat(val) || 0;
    if (v === 0) return '0.00';
    return `${formatCurrency(Math.abs(v))} ${v >= 0 ? 'Dr' : 'Cr'}`;
};

const AccountLedger = ({ ledger }) => {
    if (!ledger) return null;
    const transactions = ledger.entries || [];
    const openingEntries = ledger.openingEntries || [];
    const totalDr = parseFloat(ledger.totalDebit) || 0;
    const totalCr = parseFloat(ledger.totalCredit) || 0;
    const totalBalance = parseFloat(ledger.totalBalance) || 0;

    return (
        <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm tw-overflow-hidden tw-mb-4">
            {/* Header */}
            <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2 tw-p-3 tw-border-b tw-border-gray-100 tw-bg-gradient-to-r tw-from-rose-50/40 tw-to-pink-50/40">
                <div className="tw-flex tw-items-center tw-gap-2">
                    <h3 className="tw-text-sm tw-font-bold tw-text-gray-800">{ledger.accountName}</h3>
                    <span className="tw-text-[11px] tw-text-gray-500 tw-font-mono">({ledger.accountCode})</span>
                </div>
                <div className="tw-flex tw-items-center tw-gap-4 tw-text-[11px]">
                    <span className="tw-text-gray-500">Total Dr: <span className="tw-text-emerald-600 tw-font-semibold">{formatCurrency(totalDr)}</span></span>
                    <span className="tw-text-gray-500">Total Cr: <span className="tw-text-rose-600 tw-font-semibold">{formatCurrency(totalCr)}</span></span>
                    <span className="tw-text-gray-700 tw-font-bold">Balance: {balanceLabel(totalBalance)}</span>
                </div>
            </div>

            <div className="tw-overflow-x-auto">
                <table className="tw-w-full tw-text-xs">
                    <thead>
                        <tr className="tw-bg-gray-50 tw-border-b tw-border-gray-100">
                            <th className="tw-text-left tw-px-3 tw-py-2 tw-font-bold tw-text-gray-700 tw-text-[10px] tw-w-[40px]"></th>
                            <th className="tw-text-left tw-px-3 tw-py-2 tw-font-bold tw-text-gray-700 tw-text-[10px]">Date</th>
                            <th className="tw-text-left tw-px-3 tw-py-2 tw-font-bold tw-text-gray-700 tw-text-[10px]">Voucher No</th>
                            <th className="tw-text-left tw-px-3 tw-py-2 tw-font-bold tw-text-gray-700 tw-text-[10px]">Particulars</th>
                            <th className="tw-text-right tw-px-3 tw-py-2 tw-font-bold tw-text-gray-700 tw-text-[10px]">Dr Amount</th>
                            <th className="tw-text-right tw-px-3 tw-py-2 tw-font-bold tw-text-gray-700 tw-text-[10px]">Cr Amount</th>
                            <th className="tw-text-right tw-px-3 tw-py-2 tw-font-bold tw-text-gray-700 tw-text-[10px]">Balance</th>
                        </tr>
                    </thead>
                    <tbody className="tw-divide-y tw-divide-gray-50">
                        {/* Opening rows */}
                        {openingEntries.length > 0 ? openingEntries.map((op, idx) => (
                            <tr key={`op-${idx}`} className="tw-bg-amber-50/30">
                                <td className="tw-px-3 tw-py-2"></td>
                                <td className="tw-px-3 tw-py-2 tw-text-gray-700">{formatDate(op.date)}</td>
                                <td className="tw-px-3 tw-py-2 tw-text-gray-400">—</td>
                                <td className="tw-px-3 tw-py-2 tw-font-medium tw-text-gray-700">{op.particulars || 'Opening Balance'}</td>
                                <td className="tw-px-3 tw-py-2 tw-text-right tw-text-gray-700">
                                    {parseFloat(op.debit) > 0 ? formatCurrency(op.debit) : '-'}
                                </td>
                                <td className="tw-px-3 tw-py-2 tw-text-right tw-text-gray-700">
                                    {parseFloat(op.credit) > 0 ? formatCurrency(op.credit) : '-'}
                                </td>
                                <td className="tw-px-3 tw-py-2 tw-text-right tw-font-medium tw-text-gray-800">{balanceLabel(op.balance)}</td>
                            </tr>
                        )) : (
                            <tr className="tw-bg-amber-50/30">
                                <td className="tw-px-3 tw-py-2"></td>
                                <td className="tw-px-3 tw-py-2 tw-text-gray-400">—</td>
                                <td className="tw-px-3 tw-py-2 tw-text-gray-400">—</td>
                                <td className="tw-px-3 tw-py-2 tw-font-medium tw-text-gray-700">Opening Balance</td>
                                <td className="tw-px-3 tw-py-2 tw-text-right tw-text-gray-700">{ledger.openingBalance > 0 ? formatCurrency(ledger.openingBalance) : '-'}</td>
                                <td className="tw-px-3 tw-py-2 tw-text-right tw-text-gray-700">{ledger.openingBalance < 0 ? formatCurrency(Math.abs(ledger.openingBalance)) : '-'}</td>
                                <td className="tw-px-3 tw-py-2 tw-text-right tw-font-medium tw-text-gray-800">{balanceLabel(ledger.openingBalance)}</td>
                            </tr>
                        )}

                        {transactions.length > 0 ? transactions.map((tx, idx) => (
                            <tr key={idx} className="hover:tw-bg-slate-50/50">
                                <td className="tw-px-3 tw-py-2 tw-text-gray-400">
                                    <Icon icon="mdi:chevron-right" className="tw-text-sm" />
                                </td>
                                <td className="tw-px-3 tw-py-2 tw-text-gray-700 tw-whitespace-nowrap">{formatDate(tx.date)}</td>
                                <td className="tw-px-3 tw-py-2 tw-text-blue-700 tw-font-medium tw-whitespace-nowrap">{tx.voucherNo || '—'}</td>
                                <td className="tw-px-3 tw-py-2 tw-text-gray-700">{tx.particulars || '—'}</td>
                                <td className="tw-px-3 tw-py-2 tw-text-right tw-whitespace-nowrap">
                                    {parseFloat(tx.debit) > 0
                                        ? <span className="tw-text-emerald-600 tw-font-medium">{formatCurrency(tx.debit)}</span>
                                        : <span className="tw-text-gray-300">—</span>}
                                </td>
                                <td className="tw-px-3 tw-py-2 tw-text-right tw-whitespace-nowrap">
                                    {parseFloat(tx.credit) > 0
                                        ? <span className="tw-text-rose-600 tw-font-medium">{formatCurrency(tx.credit)}</span>
                                        : <span className="tw-text-gray-300">—</span>}
                                </td>
                                <td className="tw-px-3 tw-py-2 tw-text-right tw-font-medium tw-text-gray-800 tw-whitespace-nowrap">{balanceLabel(tx.balance)}</td>
                            </tr>
                        )) : (
                            <tr>
                                <td colSpan={7} className="tw-text-center tw-py-6 tw-text-xs tw-text-gray-400">No transactions in this period</td>
                            </tr>
                        )}
                    </tbody>
                    <tfoot>
                        <tr className="tw-bg-gray-50 tw-border-t-2 tw-border-gray-200 tw-font-semibold">
                            <td colSpan={4} className="tw-px-3 tw-py-2 tw-text-gray-700">Total</td>
                            <td className="tw-px-3 tw-py-2 tw-text-right tw-text-emerald-600">{formatCurrency(totalDr)}</td>
                            <td className="tw-px-3 tw-py-2 tw-text-right tw-text-rose-600">{formatCurrency(totalCr)}</td>
                            <td className="tw-px-3 tw-py-2 tw-text-right tw-text-gray-800">{balanceLabel(totalBalance)}</td>
                        </tr>
                    </tfoot>
                </table>
            </div>
        </div>
    );
};

export default AccountLedger;
